import kyWithSentry from './helper/kyWithSentry';
import escapeRegExp from 'helpers/escapeRegExp';
import capitalize from 'lodash.capitalize';
import getChartsListURL, {
  IChartsListURLArgs,
} from 'url-constructors/getChartsListURL';
import { ICustomer } from 'redux/reducers/customers';
import { IChartData } from 'redux/reducers/charts';

import { filterCharts } from 'components/data-containers/PositionsTable/shared/hooks/useFilteredCharts';
import { getUniqueCustomerOptions } from 'components/data-containers/PositionsTable/helper/uniqueCustomersHelpers';
import { getUniqueChartOptions } from 'components/data-containers/PositionsTable/helper/uniqueChartsHelper';

interface ICustomerWithCharts extends ICustomer {
  charts: IChartData[];
}

const firstDspCharts = ['apple music', 'spotify'];

function addFrequency(): (chart: any) => any {
  return function (chart: any): any {
    const chartUpdateFrequency = ` ${capitalize(chart.updateFrequency)}`;
    const regex = new RegExp(`${escapeRegExp(chartUpdateFrequency)}$`, 'i');

    const copy = Object.assign({}, chart);
    const { chartDisplayName } = copy;

    if (!regex.test(chartDisplayName)) {
      copy.chartDisplayName = `${chartDisplayName}${chartUpdateFrequency}`;
    } else {
      copy.chartDisplayName = chartDisplayName.replace(
        regex,
        chartUpdateFrequency
      );
    }
    return copy;
  };
}

const getChartsList = (options: IChartsListURLArgs): any =>
  kyWithSentry.get(getChartsListURL(options)).json();

const getCustomersWithcharts = (
  customers: ICustomer[],
  chartsList: IChartData[]
): ICustomerWithCharts[] => {
  return chartsList.reduce((prev: any[], current: IChartData) => {
    const findCustomerById = (customer: any): any =>
      customer.id === current.customerId;
    const addedCustomer = prev.find(findCustomerById);
    if (addedCustomer) {
      prev = prev.slice().map((customer: ICustomerWithCharts) =>
        customer.id === addedCustomer.id
          ? {
              ...customer,
              charts: [
                ...customer.charts,
                {
                  ...current,
                  displayName: `${current.chartDisplayName} ${current.updateFrequency}`,
                  name: `${current.chartDisplayName} ${current.updateFrequency}`,
                },
              ],
            }
          : customer
      );
    } else {
      prev.push({
        ...customers.find(findCustomerById),
        charts: [
          {
            ...current,
            displayName: `${current.chartDisplayName} ${current.updateFrequency}`,
            name: `${current.chartDisplayName} ${current.updateFrequency}`,
          },
        ],
      });
    }
    return prev;
  }, []);
};

const sortByPositionCount = (
  a: ICustomerWithCharts,
  b: ICustomerWithCharts
): number => b.charts.length - a.charts.length;

const sortByPositionCountOrNamePrecedence = (
  precedenceNames: string[]
): any => (a: ICustomerWithCharts, b: ICustomerWithCharts): number => {
  const idA = a.displayName.toLowerCase();
  const idB = b.displayName.toLowerCase();

  const indexA = precedenceNames.indexOf(idA);
  const indexB = precedenceNames.indexOf(idB);

  if (indexA > indexB) {
    return -1;
  }

  if (indexA < indexB) {
    return 1;
  }

  return sortByPositionCount(a, b);
};

const topChartsInterceptor = async (
  input: RequestInfo /* eslint-disable-line */,
  init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData?: any
): Promise<Response> => {
  const {
    customers,
    chartPage,
    filters,
    selectedChartDSP,
    chartFrequency,
  } = interceptionData;

  const chartsListOptions = {
    chartType: chartPage,
    sortBy: 'id',
    sortOrder: 'desc',
    limit: 999,
    productType: 'track',
    customerFilter: [selectedChartDSP],
    chartFrequency,
    customRegion: filters?.customRegion,
    region: filters?.region,
  };

  try {
    const { charts } = await getChartsList(
      chartsListOptions as IChartsListURLArgs
    );
    const filteredCharts = filterCharts({
      charts,
      filters,
    })?.map(addFrequency()) as any[];

    const uniqueCustomerOptions = getUniqueCustomerOptions({
      filteredCharts,
      customers,
    });

    const uniqueChartOptions = getUniqueChartOptions({ filteredCharts });

    const customersAndCharts = getCustomersWithcharts(customers, charts).sort(
      sortByPositionCountOrNamePrecedence(firstDspCharts)
    );

    return new Response(
      JSON.stringify({
        uniqueCustomerOptions,
        uniqueChartOptions,
        customersAndCharts,
      })
    );
  } catch (error) {
    return new Response(JSON.stringify({ reason: error.message }));
  }
};

export default topChartsInterceptor;
